import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSummary } from '|/app/services';
import useLoad from '|/hooks/use-load';

const getNextRoute = async (vehicleId: number, abortSignal: AbortSignal) => {
  const summary = await getSummary(abortSignal);
  const { vehicle: { isAvailable = null, id = null, unlockDateTime: expiresOn = null } = {} } =
    summary ?? {};

  const isLockedTimerPaused =
    expiresOn && new Date(expiresOn).getFullYear() > new Date().getFullYear() + 10;

  const hasBrowserVehicleId = vehicleId != null;
  const hasSavedVehicleId = id != null;
  const doesBrowserVehicleIdMatchSavedVehicleId =
    hasBrowserVehicleId && hasSavedVehicleId && vehicleId === id;

  if (vehicleId !== null && vehicleId === id) {
    return '/';
  }

  if (id !== null && isAvailable === false && vehicleId !== id) {
    return `/vehicle/startpurchase/${vehicleId}?isSwitch=true`;
  }

  if (vehicleId !== null && id === null) {
    return `/vehicle/startpurchase/${vehicleId}`;
  }

  if (
    hasBrowserVehicleId &&
    hasSavedVehicleId &&
    !doesBrowserVehicleIdMatchSavedVehicleId &&
    !isLockedTimerPaused
  ) {
    return `/vehicle/confirmation/${vehicleId}`;
  }

  return `/vehicle/reservation-error`;
};

const useVehiclePurchaseVm = (vehicleIdString: string | null) => {
  const load = useLoad();
  const [newRoute, setNewRoute] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const vehicleId = Number.parseInt(vehicleIdString ?? '');
    if (Number.isNaN(vehicleId)) {
      return;
    }

    const abortController = new AbortController();
    const { signal } = abortController;

    load(
      getNextRoute(vehicleId, signal).then(nextRoute => {
        if (nextRoute.includes('/vehicle/confirmation/')) {
          navigate(nextRoute, { replace: true });
        } else {
          setNewRoute(nextRoute);
        }
      })
    );

    return () => {
      abortController.abort();
    };
  }, [load, vehicleIdString, navigate]);

  return {
    newRoute
  };
};

export default useVehiclePurchaseVm;
